import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'
import { createEntry, deleteEntry, getEntry, listEntries, listMoods, updateEntry } from './api'
import type { Entry, EntryDraft, EntryPage } from './types'

export const entryKeys = {
  all: ['entries'] as const,
  list: () => [...entryKeys.all, 'list'] as const,
  detail: (id: string) => [...entryKeys.all, 'detail', id] as const,
}

export const moodKeys = {
  all: ['moods'] as const,
}

export function useEntryList() {
  return useInfiniteQuery({
    queryKey: entryKeys.list(),
    queryFn: ({ pageParam, signal }) => listEntries({ cursor: pageParam, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: EntryPage) => lastPage.nextCursor,
  })
}

export function useEntry(id: string | undefined) {
  return useQuery({
    queryKey: entryKeys.detail(id ?? ''),
    queryFn: ({ signal }) => getEntry(id as string, signal),
    enabled: Boolean(id),
  })
}

export function useMoods() {
  return useQuery({
    queryKey: moodKeys.all,
    queryFn: ({ signal }) => listMoods(signal),
    staleTime: 24 * 60 * 60_000,
  })
}

export function useCreateEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createEntry,
    onSuccess: (entry) => {
      queryClient.setQueryData(entryKeys.detail(entry.id), entry)
      void queryClient.invalidateQueries({ queryKey: entryKeys.list() })
    },
  })
}

/** 자동저장은 낙관적으로 반영하고 실패하면 직전 값으로 되돌린다. */
export function useUpdateEntry(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (draft: EntryDraft) => updateEntry(id, draft),
    onMutate: async (draft) => {
      await queryClient.cancelQueries({ queryKey: entryKeys.detail(id) })
      const previous = queryClient.getQueryData<Entry>(entryKeys.detail(id))
      if (previous) {
        queryClient.setQueryData<Entry>(entryKeys.detail(id), { ...previous, ...draft })
      }
      return { previous }
    },
    onError: (_error, _draft, context) => {
      if (context?.previous) {
        queryClient.setQueryData(entryKeys.detail(id), context.previous)
      }
    },
    onSuccess: (entry) => {
      queryClient.setQueryData(entryKeys.detail(id), entry)
      queryClient.setQueryData<InfiniteData<EntryPage, string | null>>(
        entryKeys.list(),
        (current) =>
          current && {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              items: page.items.map((item) => (item.id === entry.id ? entry : item)),
            })),
          },
      )
    },
  })
}

export function useDeleteEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteEntry,
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: entryKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: entryKeys.list() })
    },
  })
}
